import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useUserContext } from "../../utils/UserContext";
import { tincPermis } from "../../utils/Permisos";
import { API_URL } from "../../utils/config";

const PAGE_SIZES = [10, 25, 50];

function Seleccionarcategories({
  onCategoriaSelect,
  onConsultar,
  onModificar,
  onEsborrar,
  selectedCategoria,
  filterText,
  setFilterText,
  reloadToken,
  setCategoriesList,
}) {
  const { empresaSeleccionada, rolUsuari } = useUserContext();
  const [categories, setCategories] = useState([]);
  const [filteredCategories, setFilteredCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: "Categoria", direction: "asc" });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [menuObert, setMenuObert] = useState(null);
  const [menuPos, setMenuPos] = useState(null);
  const [isNarrow, setIsNarrow] = useState(false);
  const menuRef = useRef(null);

  const pucConsultar = tincPermis(rolUsuari, "CONSULTAR");
  const pucModificar = tincPermis(rolUsuari, "MODIFICAR");
  const pucEsborrar = tincPermis(rolUsuari, "ESBORRAR");

  const handleSort = (key) => {
    const direction = sortConfig.key === key && sortConfig.direction === "asc" ? "desc" : "asc";
    setSortConfig({ key, direction });
  };

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 768px)");
    const apply = () => setIsNarrow(mq.matches);
    apply();
    mq.addEventListener("change", apply);
    return () => mq.removeEventListener("change", apply);
  }, []);

  useEffect(() => {
    const fetchCategories = async () => {
      if (!empresaSeleccionada?.IdEmpresa) return;
      setLoading(true);
      try {
        const response = await fetch(`${API_URL}/Categories.php`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ idEmpresa: empresaSeleccionada.IdEmpresa }),
        });
        const result = await response.json();
        if (result.success) {
          setCategories(result.categories || []);
        } else {
          setCategories([]);
        }
      } catch (error) {
        console.error("Error carregant Categories:", error);
        setCategories([]);
      } finally {
        setLoading(false);
      }
    };

    fetchCategories();
  }, [empresaSeleccionada, reloadToken]);

  useEffect(() => {
    const lowerFilter = (filterText || "").toLowerCase();
    const filtered = (categories || []).filter((item) =>
      (item.Categoria || "").toLowerCase().includes(lowerFilter)
    );

    if (sortConfig.key) {
      filtered.sort((a, b) => {
        const aValue = (a[sortConfig.key] || "").toLowerCase();
        const bValue = (b[sortConfig.key] || "").toLowerCase();
        if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;
        return 0;
      });
    }

    setFilteredCategories(filtered);
    setCategoriesList?.(filtered);
    setPage(1);
  }, [filterText, categories, sortConfig, setCategoriesList]);

  useEffect(() => {
    const tancar = (e) => {
      if (e.target.closest(".dv2-menu") || e.target.closest(".dv2-menuBtn")) return;
      setMenuObert(null);
    };
    document.addEventListener("mousedown", tancar);
    return () => document.removeEventListener("mousedown", tancar);
  }, []);

  const obrirMenu = (e, categoria) => {
    e.stopPropagation();
    if (menuObert === categoria.idCategories) {
      setMenuObert(null);
      return;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    const ample = 200;
    const alt = 132;
    const marge = 8;
    let top = rect.bottom + 4;
    if (top + alt > window.innerHeight - marge) {
      top = Math.max(marge, rect.top - alt - 4);
    }
    let left = rect.right - ample;
    if (left < marge) left = marge;
    if (left + ample > window.innerWidth - marge) {
      left = window.innerWidth - ample - marge;
    }
    setMenuPos({ top, left });
    setMenuObert(categoria.idCategories);
  };

  const total = filteredCategories.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const paginaActual = Math.min(page, totalPages);
  const inici = (paginaActual - 1) * pageSize;
  const paginaItems = filteredCategories.slice(inici, inici + pageSize);
  const categoriaMenu = menuObert
    ? filteredCategories.find((c) => c.idCategories === menuObert)
    : null;

  const paginesVisibles = () => {
    const pages = [];
    const max = totalPages;
    const actual = paginaActual;
    const afegir = (n) => {
      if (!pages.includes(n)) pages.push(n);
    };
    afegir(1);
    for (let i = actual - 1; i <= actual + 1; i += 1) {
      if (i > 1 && i < max) afegir(i);
    }
    if (max > 1) afegir(max);
    return pages.sort((a, b) => a - b);
  };

  const renderAccions = (categoria) => (
    <div className="dv2-table-actions">
      <button
        type="button"
        className={`dv2-menuBtn ${menuObert === categoria.idCategories ? "is-open" : ""}`}
        aria-label="Accions"
        onClick={(e) => obrirMenu(e, categoria)}
      >
        …
      </button>
    </div>
  );

  return (
    <div className="dv2-listBody">
      <div className="dv2-filterBar">
        <div className="dv2-filter dv2-filter--grow">
          <span className="dv2-filter-label">Cerca</span>
          <input
            type="text"
            className="dv2-input dv2-input-search"
            placeholder="Cerca per nom de categoria…"
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
          />
        </div>
      </div>

      {loading ? (
        <p className="dv2-empty">Carregant categories…</p>
      ) : filteredCategories.length === 0 ? (
        <p className="dv2-empty">No s'ha trobat cap categoria.</p>
      ) : (
        <>
          {isNarrow ? (
          <div className="dv2-recordList" style={{ display: "flex" }}>
            {paginaItems.map((categoria) => (
              <article
                key={categoria.idCategories}
                className="dv2-record"
                onClick={() => onCategoriaSelect?.(categoria)}
                onDoubleClick={() => pucConsultar && onConsultar?.(categoria)}
              >
                <div>
                  <p className="dv2-record-title">{categoria.Categoria || "—"}</p>
                  <p className="dv2-record-meta">Categoria</p>
                </div>
                {renderAccions(categoria)}
              </article>
            ))}
          </div>
          ) : (
          <div className="dv2-tableWrap">
            <table className="dv2-table">
              <thead>
                <tr>
                  <th
                    className={sortConfig.key === "Categoria" ? "is-sorted" : ""}
                    onClick={() => handleSort("Categoria")}
                  >
                    Categoria {sortConfig.direction === "asc" ? "↑" : "↓"}
                  </th>
                  <th className="dv2-table-actions">Accions</th>
                </tr>
              </thead>
              <tbody>
                {paginaItems.map((categoria) => (
                  <tr
                    key={categoria.idCategories}
                    className={
                      selectedCategoria && selectedCategoria.idCategories === categoria.idCategories
                        ? "is-selected"
                        : ""
                    }
                    onClick={() => onCategoriaSelect?.(categoria)}
                    onDoubleClick={() => pucConsultar && onConsultar?.(categoria)}
                  >
                    <td>{categoria.Categoria || "—"}</td>
                    <td className="dv2-table-actions">{renderAccions(categoria)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          )}

          <div className="dv2-pagination">
            <span>
              Mostrant {inici + 1} a {Math.min(inici + pageSize, total)} de {total} resultats
            </span>
            <div className="dv2-pagination-pages">
              <button
                type="button"
                className="dv2-pageBtn dv2-pageBtn--nav"
                disabled={paginaActual <= 1}
                onClick={() => setPage(1)}
                aria-label="Primera pàgina"
              >
                «
              </button>
              <button
                type="button"
                className="dv2-pageBtn dv2-pageBtn--nav"
                disabled={paginaActual <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                aria-label="Pàgina anterior"
              >
                ‹
              </button>
              {paginesVisibles().map((n, idx, arr) => (
                <React.Fragment key={n}>
                  {idx > 0 && n - arr[idx - 1] > 1 && <span>…</span>}
                  <button
                    type="button"
                    className={`dv2-pageBtn ${n === paginaActual ? "is-active" : ""}`}
                    onClick={() => setPage(n)}
                  >
                    {n}
                  </button>
                </React.Fragment>
              ))}
              <button
                type="button"
                className="dv2-pageBtn dv2-pageBtn--nav"
                disabled={paginaActual >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                aria-label="Pàgina següent"
              >
                ›
              </button>
              <button
                type="button"
                className="dv2-pageBtn dv2-pageBtn--nav"
                disabled={paginaActual >= totalPages}
                onClick={() => setPage(totalPages)}
                aria-label="Última pàgina"
              >
                »
              </button>
            </div>
            <label className="dv2-filter">
              <span className="dv2-filter-label">Files per pàgina</span>
              <select
                className="dv2-select"
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setPage(1);
                }}
              >
                {PAGE_SIZES.map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </label>
          </div>
        </>
      )}
      {categoriaMenu &&
        menuPos &&
        createPortal(
          <div
            className="dv2-menu dv2-menu--fixed"
            ref={menuRef}
            style={{ top: menuPos.top, left: menuPos.left }}
          >
            {pucConsultar && (
              <button
                type="button"
                className="dv2-menu-item"
                onClick={() => {
                  setMenuObert(null);
                  onConsultar?.(categoriaMenu);
                }}
              >
                Consultar
              </button>
            )}
            {pucModificar && (
              <button
                type="button"
                className="dv2-menu-item"
                onClick={() => {
                  setMenuObert(null);
                  onModificar?.(categoriaMenu);
                }}
              >
                Modificar
              </button>
            )}
            {pucEsborrar && (
              <button
                type="button"
                className="dv2-menu-item is-danger"
                onClick={() => {
                  setMenuObert(null);
                  onEsborrar?.(categoriaMenu);
                }}
              >
                Esborrar
              </button>
            )}
          </div>,
          document.body
        )}
    </div>
  );
}

export default Seleccionarcategories;
